import { NavigationGuardNext, RouteLocationNormalizedLoaded, createRouter, createWebHistory } from 'vue-router';
import Login from '@/views/LoginWrapper/components/login.vue';
import store from '@/store';
import { Routes } from '@/router/router.const';
import i18n from '@/plugins/i18n';
import { envConfig, envFeatureFlags } from '@/services/features';

const routes = [
  {
    name: 'Home',
    path: '/',
    component: () => import('@/views/Layout/Layout.vue'),
    redirect: () => ({ name: envConfig.defaultRoute }),
    meta: {
      title: 'Home',
    },
    children: [
      {
        path: '/login-screen',
        name: 'LoginWrapper',
        component: () => import('@/views/LoginWrapper/LoginWrapper.vue'),
        meta: {
          requireAuth: false,
          hideTitle: true,
        },
        children: [
          {
            path: '/login',
            name: 'login',
            component: Login,
            meta: {
              requireAuth: false,
              hideTitle: true,
              hideNotifications: true,
              hideSideMenu: true,
              hideMargin: true,
            },
          },
          {
            path: '/enter-license',
            name: 'enterLicense',
            component: () => import('@/views/LoginWrapper/components/licenseFirstLogin.vue'),
            meta: {
              healthCheck: true,
              requireAuth: true,
              hideTitle: true,
              hideNotifications: true,
              hideSideMenu: true,
              hideMargin: true,
            },
          },
          {
            path: '/expired-license',
            name: 'licenseExpired',
            component: () => import('@/views/login/loginComponents/OS/licenseUploadComponentsLinux/expiredLicenseForm.vue'),
            meta: {
              healthCheck: true,
              requireAuth: true,
              hideTitle: true,
              hideNotifications: true,
              hideSideMenu: true,
              hideMargin: true,
            },
          },
        ],
      },
      {
        path: '/azureCallback',
        name: 'azureCallback',
        component: () => import('@/views/login/loginComponents/azure/azureCallback.vue'),
        meta: {
          requireAuth: false,
          hideTitle: true,
          hideSideMenu: true,
          hideMargin: true,
        },
        healthCheck: false,
        requireAuth: false,
        requireLicenseValid: false,
      },
      {
        path: '/dashboard',
        name: Routes.Dashboard,
        component: () => import('@/views/dashboard/dashboard.vue'),

        // redirect: () => ({ name: 'dashboard.gridUtilization' }),
        meta: {
          healthCheck: true,
          requireAuth: true,
          requireLicenseValid: true,
          dashboardFeatureFlagEnabled: true,
          title: 'Dashboard',
          hideTitle: true,
          hideMargin: true,
        },
        children: [
          {
            path: 'grid-utilization',
            name: Routes.DashboardGridUtilization,
            component: () => import('@/views/dashboard/views/DashboardGridUtilization/DashboardGridUtilization.vue'),
            meta: {
              healthCheck: true,
              requireAuth: true,
              requireLicenseValid: true,
              dashboardFeatureFlagEnabled: true,
              title: 'Dashboard',
              hideTitle: true,
              hideMargin: true,
            },
          },
          {
            path: 'builds',
            name: Routes.DashboardWorkloads,
            component: () => import('@/views/dashboard/views/DashboardWorkloads/DashboardWorkloads.vue'),
            meta: {
              healthCheck: true,
              requireAuth: true,
              requireLicenseValid: true,
              dashboardFeatureFlagEnabled: true,
              title: 'Dashboard',
              hideTitle: true,
              hideMargin: true,
            },
          },
        ],
      },
      {
        path: '/license',
        name: 'license',
        component: () => import('@/views/license/license.vue'),
        meta: {
          healthCheck: true,
          requireLicenseValid: true,
          requireAuth: true,
          title: 'License',
        },
      },
      {
        path: '/agents',
        name: 'agents',
        component: () => import('@/views/agents/agents.vue'),
        meta: {
          healthCheck: true,
          requireAuth: true,
          requireLicenseValid: true,
          title: 'Agent List',
        },
      },
      {
        path: '/builds',
        name: Routes.Builds,
        component: () => import('@/views/workloads/workloads.vue'),
        meta: {
          healthCheck: true,
          requireAuth: true,
          requireLicenseValid: true,
          title: i18n.global.t('workloads.title'),
        },
      },
      {
        path: '/builds/:id?',
        name: Routes.BuildView,
        component: () => import('@/views/workloads/workload-view/workload-view.vue'),
        props: (route: any) => ({ id: route.params.id }),
        beforeEnter: (to: RouteLocationNormalizedLoaded, _from: RouteLocationNormalizedLoaded, next: NavigationGuardNext) => {
          if (!to.params.id) {
            next({ name: Routes.Builds });
            return;
          }
          next();
        },
        meta: {
          healthCheck: true,
          requireAuth: true,
          requireLicenseValid: true,
          hideTitle: true,
          hideMargin: true,
        },
        children: [
          {
            path: '',
            name: Routes.BuildSummary,
            component: () => import('@/views/workloads/workload-view/workload-view-summary/workload-view-summary.vue'),
            props: (route: any) => ({ id: route.params.id }),
            meta: {
              hideMargin: true,
            },
          },
          {
            path: Routes.BuildPerformanceMetrics.split('.')[1],
            name: Routes.BuildPerformanceMetrics,
            component: () => import('@/views/workloads/workload-view/workload-view-performance-metrics/workload-view-performance-metrics.vue'),
            props: (route: any) => ({ id: route.params.id }),
            meta: {
              hideMargin: true,
            },
          },
        ],
      },
      {
        path: '/users',
        name: 'users',
        component: () => import('@/views/users/users.vue'),
        meta: {
          healthCheck: true,
          requireAuth: true,
          requireLicenseValid: true,
          title: 'User Management',
        },
      },
      {
        path: '/settings',
        name: 'settings',
        component: () => import('@/views/settings/settings.vue'),
        redirect: () => ({ name: 'settings.General' }),
        meta: {
          healthCheck: true,
          requireAuth: true,
          requireLicenseValid: true,
          title: 'Settings',
        },
        children: [
          {
            path: 'Agents',
            alias: '',
            name: 'settings.Agents',
            component: () => import('@/views/settings/settingsAgents.vue'),
            meta: {
              healthCheck: true,
              requireAuth: true,
              requireLicenseValid: true,
              title: 'Settings',
            },
          },
          {
            path: 'General',
            name: 'settings.General',
            component: () => import('@/views/settings/generalSettings/settingsGeneral.vue'),
            meta: {
              healthCheck: true,
              requireAuth: true,
              requireLicenseValid: true,
              title: 'Settings',
            },
          },
          {
            path: 'ApiAccess',
            name: 'settings.ApiAccess',
            component: () => import('@/views/settings/settingsApiAccess/settingsApiAccess.vue'),
            meta: {
              healthCheck: true,
              requireAuth: true,
              requireLicenseValid: true,
              title: 'Settings',
            },
          },
          {
            path: 'Cloud',
            name: 'settings.Cloud',
            component: () => import('@/views/settings/settingsCloud/settingsCloud.vue'),
            meta: {
              healthCheck: true,
              requireAuth: true,
              requireLicenseValid: true,
              title: 'Settings',
            },
          },
        ],
      },
      {
        path: '/userProfile',
        name: 'userProfile',
        component: () => import('@/views/users/userInfoProfile.vue'),
        meta: {
          healthCheck: true,
          requireAuth: true,
          requireLicenseValid: true,
          requireUserTypeCheck: true,
          hideTitle: true,
          hideMargin: true,
        },
        children: [
          {
            path: 'personalInfo',
            alias: '',
            name: 'userProfile.PersonalInfo',
            component: () => import('@/views/users/personalInfoTab.vue'),
            meta: {
              healthCheck: true,
              requireAuth: true,
              requireLicenseValid: true,
              hideTitle: true,
              hideMargin: true,
            },
          },
          {
            path: 'changePassword',
            alias: '',
            name: 'userProfile.ChangePassword',
            component: () => import('@/views/users/changePasswordTab.vue'),

            meta: {
              healthCheck: true,
              requireAuth: true,
              requireLicenseValid: true,
              hideTitle: true,
              hideMargin: true,
            },
          },
          {
            path: 'preferences',
            alias: '',
            name: 'userProfile.Preferences',
            component: () => import('@/views/users/preferencesTab.vue'),

            meta: {
              healthCheck: true,
              requireAuth: true,
              requireLicenseValid: true,
              hideTitle: true,
              hideMargin: true,
            },
          },
        ],
      },
      ...(envFeatureFlags.isFreeTrial
        ? [
            {
              path: '/getting-started',
              name: Routes.GettingStarted,
              component: () => import('@/views/getting-started/getting-started.vue'),
              meta: {
                healthCheck: true,
                requireAuth: true,
                requireLicenseValid: true,
                title: i18n.global.t('gettingStarted.title'),
              },
            },
            {
              path: '/install-agent',
              name: Routes.InstallAgent,
              component: () => import('@/views/install-agent/install-agent.vue'),
              meta: {
                healthCheck: false,
                requireAuth: false,
                requireLicenseValid: false,
                hideTitle: true,
                hideSideMenu: true,
                hideMargin: true,
              },
            },
          ]
        : []),
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  if (to.matched.find(route => route.name === 'login')) {
    store.commit('agents/tablePagination', {
      sortBy: 'name',
      descending: false,
    });
  }
  if (to.name === from.name) {
    return next(false);
  }

  if (!['login', 'enterLicense', 'azureCallback', 'null'].includes((to as any).name)) {
    sessionStorage.setItem('lastRoute', (to as any).name);
  }

  store.commit('main/closeAll');

  // if (to.meta.requireAuth) {
  //   const checkSession = store.getters['session/checkSession'];
  //   // if (!checkSession.loggedIn && store.state.idleVue.isIdle) {
  //   //   return next({ name: 'login' });
  //   // }
  // }

  // Dashboard is deprecated
  // if (to.meta.dashboardFeatureFlagEnabled) {
  //   const GQL_userLicenseInfoQuery = await apolloClient.query({
  //     query: UseGetLicenseDocument,
  //     variables: {
  //       coordinatorId: '1',
  //     },
  //   });
  //
  //   const license = GQL_userLicenseInfoQuery?.data?.licenseInfo;
  //   if (!isdashboardFeatureFlagEnabled(license)) {
  //     const defaultRoute = process.env.VUE_APP_DEFAULT_ROUTE;
  //     return next({ name: defaultRoute });
  //   }
  // }

  if (to.name === Routes.Dashboard) {
    const lastRoute = sessionStorage.getItem('dashboard.lastRoute');
    if (lastRoute) {
      return next({ name: lastRoute });
    }
    return next({ name: Routes.DashboardWorkloads });
  }
  if (to.name === Routes.DashboardWorkloads || to.name === Routes.DashboardGridUtilization) {
    sessionStorage.setItem('dashboard.lastRoute', to.name);
  }
  return next();
});

export default router;
